/**
 * Read-only view of the original signed report.
 *
 * Line breaks: the report renders in a `pre-wrap` block, so its own newlines are
 * kept as-is. Some stored reports have none — the source flattened them, often
 * to runs of spaces — and read as one run-on block. For those (and only those)
 * `sectionBreaks` finds the section headers ("Liver:", "IMPRESSION:", …) and the
 * panel inserts display-only `<br>`s before them. The text itself is never
 * rewritten: every character of `reportText` renders in order, so offsets into
 * it (the highlight below) stay valid and copy/paste matches the source.
 *
 * Highlight: when `highlightExcerpt` is set, the matching span in `reportText` is
 * highlighted and scrolled into view. Excerpts can differ from the report in
 * whitespace (the backend heuristic collapses newlines) and in case (the
 * device's quotes are verified case-insensitively), so matching is whitespace-
 * and case-tolerant rather than a literal indexOf.
 */

import React, { useEffect, useMemo, useRef } from "react";

interface OriginalReportPanelProps {
  reportText: string;
  highlightExcerpt?: string | null;
}

export function findExcerptRange(text: string, excerpt: string): [number, number] | null {
  const trimmed = excerpt.trim();
  if (!trimmed || !text) return null;
  const pattern = trimmed.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, "\\s+");
  const m = text.match(new RegExp(pattern, "i"));
  if (!m || m.index === undefined) return null;
  return [m.index, m.index + m[0].length];
}

export interface SectionBreak {
  /** Offset in the report text the header starts at; the break goes before it. */
  offset: number;
  /** ALL-CAPS headers (FINDINGS:, IMPRESSION:) get a blank line, not just a break. */
  major: boolean;
}

// A header: up to six single-space-separated words, the first capitalised, then
// a colon. Words are single-space separated so a header never spans a flattened
// line break (a run of 2+ spaces).
const HEADER = String.raw`[A-Z][A-Za-z0-9/&()'-]*(?: [A-Za-z0-9/&()'-]+){0,5}:`;
// Break before any header that follows a run of 2+ whitespace characters (where
// the source's line breaks were flattened) …
const AFTER_GAP = new RegExp(String.raw`\s{2,}(?=(${HEADER}))`, "g");
// … and before an ALL-CAPS header (FINDINGS:, IMPRESSION:) after a single
// space that ends a sentence or field — not after a capitalised word, which
// would split a multi-word header ("CLINICAL INDICATION:") in two.
const CAPS_HEADER = String.raw`[A-Z][A-Z0-9/&()'-]+(?: [A-Z0-9/&()'-]+){0,5}:`;
const BEFORE_CAPS = new RegExp(String.raw`(?<=[.;:)\]])\s+(?=(${CAPS_HEADER}))`, "g");

function isAllCaps(header: string): boolean {
  return /[A-Z]{2}/.test(header) && header === header.toUpperCase();
}

/**
 * Display-only break points for a report with no line breaks of its own. Empty
 * for a report that has any — its own layout wins.
 */
export function sectionBreaks(text: string): SectionBreak[] {
  if (!text || /[\r\n]/.test(text)) return [];
  const byOffset = new Map<number, SectionBreak>();
  for (const re of [AFTER_GAP, BEFORE_CAPS]) {
    for (const m of text.matchAll(re)) {
      const offset = (m.index ?? 0) + m[0].length;
      if (offset === 0 || offset >= text.length) continue;
      byOffset.set(offset, { offset, major: isAllCaps(m[1]) });
    }
  }
  return [...byOffset.values()].sort((a, b) => a.offset - b.offset);
}

/** Report text as React nodes: section breaks inserted, the highlight wrapped.
 * Every character of `text` is emitted once, in order. */
function renderReport(
  text: string,
  breaks: SectionBreak[],
  range: [number, number] | null,
  matchRef: React.RefObject<HTMLSpanElement | null>,
): React.ReactNode[] {
  const cuts = new Set<number>([0, text.length]);
  for (const b of breaks) cuts.add(b.offset);
  if (range) {
    cuts.add(range[0]);
    cuts.add(range[1]);
  }
  const points = [...cuts].sort((a, b) => a - b);
  const breakAt = new Map(breaks.map((b) => [b.offset, b]));
  const nodes: React.ReactNode[] = [];
  let firstHighlight = true;
  for (let i = 0; i < points.length - 1; i++) {
    const [start, end] = [points[i], points[i + 1]];
    const br = breakAt.get(start);
    if (br) {
      nodes.push(<br key={`br-${start}`} />);
      if (br.major) nodes.push(<br key={`br2-${start}`} />);
    }
    const chunk = text.slice(start, end);
    if (range && start >= range[0] && end <= range[1]) {
      nodes.push(
        <span
          key={`hl-${start}`}
          ref={firstHighlight ? matchRef : undefined}
          data-testid="report-highlight"
          className="bg-signal-tint text-signal-text-tint rounded-xs px-0.5 transition-colors"
        >
          {chunk}
        </span>,
      );
      firstHighlight = false;
    } else {
      nodes.push(<React.Fragment key={`t-${start}`}>{chunk}</React.Fragment>);
    }
  }
  return nodes;
}

const OriginalReportPanel: React.FC<OriginalReportPanelProps> = ({
  reportText,
  highlightExcerpt,
}) => {
  const matchRef = useRef<HTMLSpanElement>(null);

  const range = useMemo(
    () => (highlightExcerpt ? findExcerptRange(reportText, highlightExcerpt) : null),
    [reportText, highlightExcerpt],
  );
  const breaks = useMemo(() => sectionBreaks(reportText), [reportText]);
  const hasMatch = range !== null;

  useEffect(() => {
    if (hasMatch && matchRef.current) {
      matchRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [hasMatch, highlightExcerpt]);

  return (
    <div className="h-full flex flex-col bg-bg-2">
      <div className="px-3 py-2 border-b border-border-strong flex items-center gap-2 bg-surface-2">
        <span className="text-[11px] font-bold uppercase tracking-widest text-fg-dim">
          Original Report
        </span>
        <span className="ml-auto text-[10px] text-fg-faint uppercase tracking-wider">
          Signed · read-only
        </span>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-2.5">
        {reportText ? (
          <pre
            data-testid="original-report-text"
            className="whitespace-pre-wrap text-[12px] leading-relaxed text-fg-muted font-mono"
          >
            {renderReport(reportText, breaks, range, matchRef)}
          </pre>
        ) : (
          <div className="text-sm text-fg-faint text-center py-8">
            No report text available for this study.
          </div>
        )}
      </div>
    </div>
  );
};

export default OriginalReportPanel;
